import { getSheetsByReportType } from "../../workbook/customProperties";
import { AREA_SHEET_REPORT_NAME, ROOMS_REPORT_NAME, UNIT_MIX_REPORT_NAME } from "../api/reportNames";
import { createAlternatesSOVTemplate } from "./alternatesSOV";
import { generateAreaSheetReportsFromTemplate } from "./areaSheet";
import { createDefaultEstimateTemplate } from "./estimate";
import { createDefaultGCGRTemplate, createDefaultStaffDetailTemplate } from "./gcgr";
import { createMarkupsTemplate } from "./markups";
import { generateRoomsReportsFromTemplate } from "./rooms";
import { createSOVTemplate } from "./sov";
import { generateUnitMixReportsFromTemplate } from "./unitMix";
import { createVMSummaryTemplate } from "./vmSummary";
import { ReportGroup } from "../api/groups";

export interface VisibilityContext {
  hasEstimateItems: boolean;
  hasAlternateItems: boolean;
  hasMarkups: boolean;
  hasVMSummary: boolean;
  hasAreaSheet: boolean;
  hasBidTabs: boolean;
  hasGCGRs: boolean;
  hasGCGRLineItems: boolean;
  hasUnitMix: boolean;
  hasRooms: boolean;
}

export interface EdiphiReportTemplate {
  name: string;
  /** Which section of the Reports drawer this is offered under. */
  group: ReportGroup;
  templateBuilder: () => Promise<void>;
  renderIf: (ctx: VisibilityContext) => boolean;
  isMultiSheet?: boolean;
}

export const ediphiReportTemplates: Record<string, EdiphiReportTemplate> = {
  estimate: {
    group: "estimate",
    name: "Detailed Estimate",
    templateBuilder: () => createDefaultEstimateTemplate("Estimate"),
    renderIf: (ctx) => ctx.hasEstimateItems,
  },
  alternates: {
    group: "estimate",
    name: "VM Decisions Estimate",
    templateBuilder: () => createDefaultEstimateTemplate("VM Decisions"),
    renderIf: (ctx) => ctx.hasAlternateItems,
  },
  estimateSOV: {
    group: "estimate",
    name: "Estimate SOV",
    templateBuilder: () => createSOVTemplate("Estimate"),
    renderIf: (ctx) => ctx.hasEstimateItems,
  },
  crossTabSOV: {
    group: "estimate",
    name: "Estimate Cross Tab SOV",
    templateBuilder: () => createSOVTemplate("Estimate", true),
    renderIf: (ctx) => ctx.hasEstimateItems,
  },
  alternatesSOV: {
    group: "estimate",
    name: "VM Decisions SOV",
    templateBuilder: () => createAlternatesSOVTemplate(),
    renderIf: (ctx) => ctx.hasAlternateItems,
  },
  contractingSOV: {
    group: "contracting",
    name: "Contracting SOV",
    templateBuilder: () => createSOVTemplate("Contracting"),
    renderIf: (ctx) => ctx.hasBidTabs,
  },
  gcgrStaffDetail: {
    group: "gcgr",
    name: "GCGR Staff Detail",
    templateBuilder: () => createDefaultStaffDetailTemplate(),
    renderIf: (ctx) => ctx.hasGCGRs,
  },
  gcgrLineItemDetail: {
    group: "gcgr",
    name: "GCGR Estimate",
    templateBuilder: () => createDefaultGCGRTemplate(),
    // A GCGR can exist with staffing but zero detail line items — gate on the line items
    // themselves (main gated on dataExists("GCGRLineItems"))
    renderIf: (ctx) => ctx.hasGCGRLineItems,
  },
  areaSheet: {
    group: "estimate",
    name: AREA_SHEET_REPORT_NAME,
    templateBuilder: async () => {
      await generateAreaSheetReportsFromTemplate();
      await Excel.run(async (context) => {
        const reports = await getSheetsByReportType(context, AREA_SHEET_REPORT_NAME);
        if (reports.length > 0) {
          const firstSheet = reports[0].worksheet;
          firstSheet.activate();
          await context.sync();
        }
      });
    },
    renderIf: (ctx) => ctx.hasAreaSheet,
    isMultiSheet: true,
  },
  vmSummary: {
    group: "estimate",
    name: "VM Summary",
    templateBuilder: () => createVMSummaryTemplate(),
    renderIf: (ctx) => ctx.hasVMSummary,
  },
  markups: {
    group: "estimate",
    name: "Markups",
    templateBuilder: () => createMarkupsTemplate(),
    renderIf: (ctx) => ctx.hasMarkups,
  },
  unitMix: {
    group: "estimate",
    name: UNIT_MIX_REPORT_NAME,
    templateBuilder: async () => {
      await generateUnitMixReportsFromTemplate();
      await Excel.run(async (context) => {
        const reports = await getSheetsByReportType(context, UNIT_MIX_REPORT_NAME);
        if (reports.length > 0) {
          const firstSheet = reports[0].worksheet;
          firstSheet.activate();
          await context.sync();
        }
      });
    },
    renderIf: (ctx) => ctx.hasUnitMix,
    isMultiSheet: true,
  },
  rooms: {
    group: "estimate",
    name: ROOMS_REPORT_NAME,
    templateBuilder: async () => {
      await generateRoomsReportsFromTemplate();
      await Excel.run(async (context) => {
        const reports = await getSheetsByReportType(context, ROOMS_REPORT_NAME);
        if (reports.length > 0) {
          const firstSheet = reports[0].worksheet;
          firstSheet.activate();
          await context.sync();
        }
      });
    },
    renderIf: (ctx) => ctx.hasRooms,
    isMultiSheet: true,
  },
};
